import { PageHead, Panel } from '../components/ui';

type Level = 'strong' | 'mid' | 'weak';
const LEVEL: Record<Level, string> = { strong: '근거 강함', mid: '근거 중간', weak: '근거 약함 · 논쟁' };

const METHODS: { title: string; level: Level; body: string[]; apex?: string }[] = [
  {
    title: '1. 다시 읽지 말고 떠올려 본다 (인출 연습)', level: 'strong',
    body: ['책을 덮고 방금 읽은 내용을 떠올린다. "무슨 내용이었지?" 하고 스스로 묻는 것만으로도 된다.', '다시 읽을 때보다 직접 꺼내려고 애쓸 때 훨씬 오래 남는다. 떠올리다 막힌 곳이 바로 다시 볼 곳이다.'],
    apex: '복습 카드는 답을 먼저 쓰고 나서 대조하게 되어 있다.',
  },
  {
    title: '2. 한꺼번에 말고 간격을 두고 복습한다', level: 'strong',
    body: ['같은 날 연달아 읽기보다 날을 띄워 다시 떠올린다 (예: 다음 날 → 3일 뒤 → 1주 뒤 → 한 달 뒤).', '조금 잊었을 때 다시 꺼내는 수고가 기억을 단단하게 만든다. 오래 기억해야 할수록 간격도 길게 잡는다.'],
    apex: '복습 날짜는 APEX 가 기억 모델(FSRS)로 카드마다 자동으로 잡는다.',
  },
  {
    title: '3. 중요한 내용은 줄여서 내 말로 적는다', level: 'mid',
    body: [
      '손으로 쓰면 다 받아 적을 수 없어서 저절로 줄이고 바꿔 쓰게 된다 — 효과의 핵심은 이 "줄이고 내 말로 바꾸기"다.',
      '2024년 뇌파 연구는 손으로 쓸 때 뇌의 더 넓은 영역이 함께 움직인다는 결과로, 기억 점수를 직접 잰 연구는 아니다. 노트북 필기와 손 필기를 비교한 연구들도 결과가 엇갈린다. 타이핑이라도 받아쓰지 않고 요약하면 비슷한 효과를 기대할 수 있다.',
    ],
    apex: "'새 개념'은 정의·원리·활용·반례를 내 말로 적게 되어 있다.",
  },
  {
    title: "4. 단락마다 '왜?'라고 묻는다", level: 'mid',
    body: ['왜 이런 결과가 나왔을까? 내가 아는 것과 어떻게 이어질까? 다른 사람에게는 어떻게 설명할까?', '이유를 스스로 설명해 보면 그냥 다시 읽을 때보다 깊이 이해하고 오래 남는다.'],
  },
  {
    title: '5. 공부 전에 가볍게 몸을 움직인다', level: 'weak',
    body: [
      '빠르게 걷기·자전거 정도로 20분쯤 움직인 뒤 기억 과제 성적이 조금 좋아졌다는 연구들이 있다. 다만 효과는 작고 연구마다 들쭉날쭉하다.',
      '운동하면 BDNF(뇌세포 사이 연결을 돕는 단백질)가 늘어나는 것은 맞지만, 그것이 사람의 학습을 얼마나 돕는지는 주로 동물 연구에서 나온 설명이다. 해 볼 만한 습관 정도로 보면 된다.',
    ],
  },
  {
    title: '6. 공부한 날은 충분히 잔다', level: 'strong',
    body: ['자는 동안, 특히 깊은 잠에서 뇌는 낮에 익힌 내용을 되풀이하며 오래 가는 기억으로 옮긴다.', '밤을 새우면 그날 공부한 것도 덜 남고, 다음 날 새로 배우는 힘도 떨어진다.'],
  },
  {
    title: '7. 다른 사람에게 설명해 본다', level: 'mid',
    body: ['가르친다고 생각하면 더 꼼꼼하게 정리하게 된다. 설명하다 막히는 곳이 제대로 모르는 곳이다.', '효과의 큰 부분은 설명하면서 내용을 떠올리는 데서 나온다. 사람이 없으면 혼자 소리 내어 설명해도 된다.'],
  },
  {
    title: '8. 공부할 때는 한 가지에만 집중한다', level: 'strong',
    body: ['휴대전화 알림, 주변 대화, 여러 일을 번갈아 하는 습관을 치운다.', '주의가 나뉘면 처음부터 기억에 잘 새겨지지 않는다. 제대로 받아들이지 못한 정보는 나중에 꺼낼 수도 없다.'],
  },
];

const ROUTINE = [
  '알림을 끄고 한 가지만 읽는다.',
  '읽으면서 중요한 것만 짧게, 내 말로 적는다.',
  '다 읽으면 책과 메모를 덮고 핵심을 떠올려 세 줄로 적는다.',
  "중요한 개념은 APEX '새 개념'에 내 말로 정리한다 → 복습 카드가 생긴다.",
  '그날 밤 충분히 잔다.',
  '24~48시간 뒤 첫 복습 — 날짜는 APEX 가 알려 준다.',
  '누군가에게(또는 혼자 소리 내어) 설명해 본다.',
];

/** 읽은 내용을 오래 기억하는 법 — 근거 수준을 함께 적은 공부법 안내 */
export function Guide() {
  return (
    <>
      <PageHead title="오래 기억하는 법" sub="읽기·공부 요령 — 근거가 얼마나 단단한지 함께 적었습니다" />
      <Panel title="그냥 읽기만 하면 왜 잊을까">
        <div className="guide-text">
          <p>글을 읽으면 내용은 먼저 <b>작업기억</b>에 들어온다. 작업기억은 몇 가지를 잠깐 붙잡아 두는 곳이라 곧 사라진다. 오래 남기려면 뇌(특히 <b>해마</b>)가 그 내용을 장기기억으로 옮겨 굳혀야 한다.</p>
          <p>이때 중요한 것은 읽은 <b>횟수</b>가 아니라 얼마나 <b>주의를 기울이고, 의미를 따져 보고, 스스로 떠올려 보았는가</b>다. 감정이 실리거나 깊이 생각한 내용이 잘 남는 것도 같은 까닭이다.</p>
        </div>
      </Panel>
      <div className="guide-list">
        {METHODS.map((m) => (
          <Panel key={m.title}>
            <h2 className="guide-title">{m.title} <span className={`guide-level ${m.level}`}>{LEVEL[m.level]}</span></h2>
            <div className="guide-text">
              {m.body.map((b) => <p key={b}>{b}</p>)}
              {m.apex && <p className="guide-apex">APEX: {m.apex}</p>}
            </div>
          </Panel>
        ))}
      </div>
      <Panel title="형광펜과 다시 읽기는 왜 효과가 낮나">
        <div className="guide-text">
          <p>형광펜을 칠하고 같은 내용을 여러 번 읽으면 공부를 많이 한 느낌이 든다. 하지만 떠올리게도, 깊이 생각하게도 만들지 않아 효과가 낮다 (공부법들의 효과를 비교한 큰 연구 정리에서 둘 다 '낮음').</p>
          <p>여러 번 봐서 <b>익숙한 것</b>과 필요할 때 <b>꺼낼 수 있는 것</b>은 다르다. "본 적 있다"는 느낌을 "안다"로 착각하지 않는다.</p>
        </div>
      </Panel>
      <Panel title="가장 효과적인 순서">
        <ol className="guide-routine">{ROUTINE.map((r) => <li key={r}>{r}</li>)}</ol>
        <p className="small muted">
          근거: Dunlosky 외(2013) 공부법 10가지 효과 비교 · Roediger & Karpicke(2006) 인출 연습 · Cepeda 외(2006) 간격 효과 메타분석 ·
          수면과 기억 굳히기 연구들 · Van der Weel & Van der Meer(2024) 손글씨 뇌파 연구 · Mueller & Oppenheimer(2014) 손 필기 연구와 재현 연구(Urry 외, 2021).
        </p>
        <div className="row-gap">
          <a className="btn btn-primary" href="#/knowledge/new">새 개념 정리하기</a>
          <a className="btn" href="#/review">복습하기</a>
        </div>
      </Panel>
    </>
  );
}
